import { createContext, useContext, useEffect, type MouseEvent, type ReactNode } from 'react';
import { useUiLabels, UiLink } from '../labels';
import { cn } from '../lib/cn';
import { Icon, type IconName } from './icon';

export interface Breadcrumb {
  label: string;
  href?: string;
}

/** The level above a page: the last breadcrumb with a link before the page itself. */
export function parentOf(items: readonly Breadcrumb[]): Breadcrumb | null {
  for (let index = items.length - 2; index >= 0; index -= 1) {
    const item = items[index]!;
    if (item.href) return item;
  }
  return null;
}

export interface PageParentHandlers {
  /** The page header reports the level above the page, `null` when it is gone. */
  report: (parent: Breadcrumb | null) => void;
  /**
   * Called before a back link follows its href. Returns `true` when it went back itself, e.g. in
   * the history to the list the person came from, so filters and scroll position stay.
   */
  back: (parent: Breadcrumb) => boolean;
}

const PageParentContext = createContext<PageParentHandlers | null>(null);

/**
 * Set by the app frame: it shows the way back of the current page itself on the phone (top bar)
 * and decides how to go back. Without it, breadcrumbs and back links work as plain links.
 */
export function PageParentProvider({
  value,
  children,
}: {
  value: PageParentHandlers;
  children: ReactNode;
}) {
  return <PageParentContext.Provider value={value}>{children}</PageParentContext.Provider>;
}

function useBackClick(parent: Breadcrumb | null) {
  const handlers = useContext(PageParentContext);
  return (event: MouseEvent<HTMLAnchorElement>) => {
    if (!parent || !handlers) return;
    // New tab and the like stay with the browser.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }
    if (handlers.back(parent)) event.preventDefault();
  };
}

/**
 * „‹ Ansprechpartner“: back to the level above, as in iOS apps. The app frame shows it in the top
 * bar on the phone; the label names where it leads, screen readers hear „Zurück“ in front of it.
 */
export function BackLink({ parent, className }: { parent: Breadcrumb; className?: string }) {
  const labels = useUiLabels();
  const onClick = useBackClick(parent);
  return (
    <UiLink
      href={parent.href!}
      onClick={onClick}
      aria-label={`${labels.back}: ${parent.label}`}
      className={cn(
        'flex h-11 min-w-0 cursor-pointer items-center gap-0.5 rounded-md pr-2 font-medium text-brand-text transition-colors transition-fast active:bg-pressed',
        className,
      )}
    >
      <Icon name="chevron-left" className="size-6 shrink-0" />
      <span className="truncate">{parent.label}</span>
    </UiLink>
  );
}

export function Breadcrumbs({
  items,
  className,
}: {
  items: readonly Breadcrumb[];
  className?: string;
}) {
  const labels = useUiLabels();
  const shell = useContext(PageParentContext);
  const parent = parentOf(items);
  const onBack = useBackClick(parent);
  if (items.length === 0) return null;
  return (
    <nav
      aria-label={labels.breadcrumbs}
      // On the phone the app frame shows the way back in the top bar instead.
      className={cn(
        'flex min-w-0 items-center gap-2',
        shell && parent && 'max-md:hidden',
        className,
      )}
    >
      {parent && (
        <UiLink
          href={parent.href!}
          onClick={onBack}
          aria-label={`${labels.back}: ${parent.label}`}
          title={`${labels.back}: ${parent.label}`}
          // 28 px to see, 44 px to hit (WCAG 2.5.8, iPad).
          className="relative -ml-1.5 flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-fg-muted transition-colors transition-fast after:absolute after:-inset-2 hover:bg-hover hover:text-fg active:bg-pressed"
        >
          <Icon name="arrow-left" className="size-4" />
        </UiLink>
      )}
      <ol className="flex min-w-0 items-center gap-1 text-xs text-fg-subtle">
        {items.map((item, index) => {
          const last = index === items.length - 1;
          return (
            <li key={`${item.label}-${index}`} className="flex min-w-0 items-center gap-1">
              {item.href && !last ? (
                <UiLink
                  href={item.href}
                  onClick={item === parent ? onBack : undefined}
                  className="truncate rounded-sm hover:text-fg hover:underline"
                >
                  {item.label}
                </UiLink>
              ) : (
                <span aria-current={last ? 'page' : undefined} className="truncate">
                  {item.label}
                </span>
              )}
              {!last && <Icon name="chevron-right" className="size-3" />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

const PageHeaderAddonContext = createContext<ReactNode>(null);

/**
 * Content the app shows in every page header in front of the page's own actions, e.g. the
 * guide button of the current page. Pages set nothing for it.
 */
export function PageHeaderAddon({ value, children }: { value: ReactNode; children: ReactNode }) {
  return (
    <PageHeaderAddonContext.Provider value={value}>{children}</PageHeaderAddonContext.Provider>
  );
}

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  breadcrumbs?: readonly Breadcrumb[];
  /** Module icon in the module accent. */
  icon?: IconName;
  /** Further actions next to the title (secondary buttons, settings gear). */
  actions?: ReactNode;
  /**
   * The most important action of the page. In the header on tablet and desktop, fixed at the
   * bottom above the navigation on the phone (docs/ui/responsive.md).
   */
  primaryAction?: ReactNode;
  /** Line below the title: counts, status, last change. */
  meta?: ReactNode;
  /**
   * Description also on the phone. Off for pages whose content should start right away, e.g. a
   * list with filters; the description is then only on tablet and desktop.
   */
  descriptionOnPhone?: boolean;
  className?: string;
}

export function PageHeader({
  title,
  description,
  breadcrumbs,
  icon,
  actions,
  primaryAction,
  meta,
  descriptionOnPhone = true,
  className,
}: PageHeaderProps) {
  const addon = useContext(PageHeaderAddonContext);
  const shell = useContext(PageParentContext);
  const parent = breadcrumbs ? parentOf(breadcrumbs) : null;
  const report = shell?.report;
  const parentHref = parent?.href;
  const parentLabel = parent?.label;
  // Tells the app frame where back leads, for the top bar on the phone and the swipe from the edge.
  useEffect(() => {
    if (!report) return;
    report(parentHref && parentLabel ? { label: parentLabel, href: parentHref } : null);
    return () => report(null);
  }, [report, parentHref, parentLabel]);
  const side = Boolean(addon || actions || primaryAction);
  return (
    <header className={cn('flex flex-col gap-2 pb-5', className)}>
      {breadcrumbs && <Breadcrumbs items={breadcrumbs} />}
      {/* Icon, title and actions in the first row. Description and meta below, also under the
          actions, so wide actions do not squeeze them into a narrow column next to them. */}
      <div
        className={cn(
          'grid items-start gap-x-3',
          icon && side && 'grid-cols-[auto_minmax(0,1fr)_auto]',
          icon && !side && 'grid-cols-[auto_minmax(0,1fr)]',
          !icon && side && 'grid-cols-[minmax(0,1fr)_auto]',
          !icon && !side && 'grid-cols-1',
        )}
      >
        {icon && (
          <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-md bg-accent-subtle text-accent">
            <Icon name={icon} className="size-5" />
          </div>
        )}
        <h1 className="min-w-0 text-xl font-semibold tracking-tight text-balance text-fg md:text-2xl">
          {title}
        </h1>
        {side && (
          <div className="flex shrink-0 items-center gap-2">
            {addon}
            {actions}
            {primaryAction && <div className="hidden md:contents">{primaryAction}</div>}
          </div>
        )}
        {(description || meta) && (
          <div
            className={cn(
              'min-w-0',
              !descriptionOnPhone && !meta && 'max-md:hidden',
              icon ? 'col-start-2 col-end-[-1]' : 'col-start-1 col-end-[-1]',
            )}
          >
            {description && (
              <p
                className={cn(
                  'mt-1 max-w-3xl text-fg-muted',
                  !descriptionOnPhone && 'max-md:hidden',
                )}
              >
                {description}
              </p>
            )}
            {meta && (
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-fg-subtle">
                {meta}
              </div>
            )}
          </div>
        )}
      </div>
      {primaryAction && (
        <div className="fixed inset-x-4 bottom-[calc(var(--bottombar-h)+env(safe-area-inset-bottom)+0.75rem)] z-30 md:hidden [&>*]:w-full [&>*]:shadow-overlay">
          {primaryAction}
        </div>
      )}
    </header>
  );
}

/**
 * One fact in `PageHeader.meta`, e.g. the object or the client of a record: an icon says what it
 * is, for screen readers the label does. Keeps a row of names readable without „Objekt:“ in front
 * of each.
 */
export function MetaItem({
  icon,
  label,
  children,
  className,
}: {
  icon: IconName;
  /** What the value is, e.g. „Kunde“; read before the value and shown as tooltip of the icon. */
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span className={cn('inline-flex max-w-full min-w-0 items-center gap-1.5', className)}>
      <span title={label} className="flex shrink-0">
        <Icon name={icon} className="size-3.5" />
      </span>
      <span className="sr-only">{label}:</span>
      <span className="min-w-0">{children}</span>
    </span>
  );
}

/** Section within a page: heading, optional text and actions, content. */
export function PageSection({
  title,
  description,
  actions,
  children,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('flex flex-col gap-3', className)}>
      {/* On the phone the actions go below the title, so a wide field does not squeeze it. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-fg">{title}</h2>
          {description && <p className="mt-0.5 text-fg-muted">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2 sm:shrink-0">{actions}</div>}
      </div>
      {children}
    </section>
  );
}
